// Hover / tap panel for the Graded column of the Palworld TCG price chart.
//
// A certified slab is a different product to the raw single, so the graded cell
// opens its own panel with the graded market median, the graded listing range,
// how many slab listings the median is built from, and direct eBay links to the
// live Buy It Now / auction listings and the PSA
// population report. Rendered into a body portal so the table's horizontal
// scroll container cannot clip it.
import { useCallback, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { formatMoney, GRADING_SOURCES, type CurrencyCode, type PriceRow } from "@/lib/prices-shared";
import { ebayAffiliateUrl } from "@/lib/ebay-affiliate";
import { trackAffiliateClick } from "@/lib/affiliate-clicks";

const q = (s: string) => encodeURIComponent(`${s} -japanese -korean -chinese -jpn -jp`);

/** Graded eBay deep links: live listings and auctions only. */
function gradedLinks(name: string, code: string) {
  const nkw = q(`Palworld ${name} ${code.replace(/^E/i, "")} PSA`);
  return [
    {
      id: "ebay-graded",
      label: "Graded listings",
      href: `https://www.ebay.com/sch/i.html?_nkw=${nkw}&_sop=15`,
      note: "Live PSA / BGS / CGC slabs, cheapest first.",
      primary: true,
    },
    {
      id: "ebay-graded",
      label: "Graded auctions",
      href: `https://www.ebay.com/sch/i.html?_nkw=${nkw}&LH_Auction=1&_sop=1`,
      note: "Auctions only, ending soonest — where graded chase cards set the price.",
      primary: false,
    },
  ];
}

export function GradedQuoteHover({
  row,
  currency,
  className = "",
}: {
  row: PriceRow;
  currency: CurrencyCode;
  className?: string;
}) {
  const [open, setOpen] = useState(false);
  const [mounted, setMounted] = useState(false);
  const [pos, setPos] = useState<{ left: number; top: number } | null>(null);
  const chip = useRef<HTMLButtonElement>(null);
  const panel = useRef<HTMLDivElement>(null);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => setMounted(true), []);

  const clear = () => {
    if (timer.current) {
      clearTimeout(timer.current);
      timer.current = null;
    }
  };
  const later = () => {
    clear();
    timer.current = setTimeout(() => setOpen(false), 220);
  };
  useEffect(() => clear, []);

  const place = useCallback(() => {
    const r = chip.current?.getBoundingClientRect();
    if (!r) return;
    const w = 300;
    const h = 280;
    const above = r.top > h + 12;
    setPos({
      left: Math.min(Math.max(r.left + r.width / 2 - w / 2, 8), window.innerWidth - w - 8),
      top: above ? r.top - 8 - h : r.bottom + 8,
    });
  }, []);

  const show = () => {
    clear();
    place();
    setOpen(true);
  };

  useEffect(() => {
    if (!open) return;
    const onDown = (e: PointerEvent) => {
      const t = e.target as Node;
      if (!chip.current?.contains(t) && !panel.current?.contains(t)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => e.key === "Escape" && setOpen(false);
    const onMove = () => setOpen(false);
    document.addEventListener("pointerdown", onDown);
    window.addEventListener("keydown", onKey);
    window.addEventListener("scroll", onMove, { passive: true });
    window.addEventListener("resize", onMove);
    return () => {
      document.removeEventListener("pointerdown", onDown);
      window.removeEventListener("keydown", onKey);
      window.removeEventListener("scroll", onMove);
      window.removeEventListener("resize", onMove);
    };
  }, [open]);

  const has = row.gradedAvg !== null;
  const links = gradedLinks(row.name, row.code);
  const multiple = has && row.market > 0 ? Math.round(((row.gradedAvg as number) / row.market) * 10) / 10 : null;

  return (
    <span className={`inline-block ${className}`}>
      <button
        ref={chip}
        type="button"
        onMouseEnter={show}
        onMouseLeave={later}
        onFocus={show}
        onClick={(e) => {
          e.stopPropagation();
          if (open) setOpen(false);
          else show();
        }}
        aria-expanded={open}
        aria-label={`Graded (PSA, BGS, CGC) listings and sold prices for ${row.name} ${row.code}`}
        className={`rounded-md px-1.5 py-0.5 tabular-nums transition hover:bg-amber-400/10 ${
          has ? "font-semibold text-amber-300" : "text-muted-foreground"
        }`}
      >
        {has ? formatMoney(row.gradedAvg as number, currency) : "—"}
      </button>

      {mounted && open && pos
        ? createPortal(
            <div
              ref={panel}
              role="dialog"
              aria-label={`${row.name} ${row.code} graded market`}
              onMouseEnter={clear}
              onMouseLeave={later}
              style={{ left: pos.left, top: pos.top, width: 300 }}
              className="fixed z-[130] rounded-xl border border-border/70 bg-background/95 p-3 text-left shadow-2xl backdrop-blur"
            >
              <p className="text-xs font-bold">
                Graded slabs · {row.name}{" "}
                <span className="font-mono text-[10px] text-muted-foreground">{row.code}</span>
              </p>
              {has ? (
                <dl className="mt-2 grid grid-cols-2 gap-x-3 gap-y-1 text-[11px]">
                  <dt className="text-muted-foreground">Graded market</dt>
                  <dd className="text-right font-semibold tabular-nums text-amber-300">
                    {formatMoney(row.gradedAvg as number, currency)}
                  </dd>
                  <dt className="text-muted-foreground">Graded range</dt>
                  <dd className="text-right tabular-nums">
                    {row.gradedLow && row.gradedHigh
                      ? `${formatMoney(row.gradedLow, currency)}–${formatMoney(row.gradedHigh, currency)}`
                      : "single listing"}
                  </dd>
                  <dt className="text-muted-foreground">Slab listings</dt>
                  <dd className="text-right tabular-nums">{row.gradedCount || 1}</dd>
                  <dt className="text-muted-foreground">Raw copy</dt>
                  <dd className="text-right tabular-nums">{formatMoney(row.market, currency)}</dd>
                  {multiple ? (
                    <>
                      <dt className="text-muted-foreground">Graded premium</dt>
                      <dd className="text-right tabular-nums">{multiple}x raw</dd>
                    </>
                  ) : null}
                </dl>
              ) : (
                <p className="mt-2 text-[11px] leading-snug text-muted-foreground">
                  No validated slab quote is stored for this printing yet. Open the live graded searches below — a PSA
                  10, BGS or CGC copy listed right now (including single-population slabs) appears here on the next
                  daily sync.
                </p>
              )}

              <div className="mt-2 flex flex-wrap gap-1.5">
                {links.map((l) => (
                  <a
                    key={l.label}
                    href={ebayAffiliateUrl(l.href, `${row.code}-graded`)}
                    target="_blank"
                    rel="nofollow ugc noopener noreferrer"
                    title={l.note}
                    onClick={(e) => {
                      e.stopPropagation();
                      trackAffiliateClick({ source: l.id, kind: "card", targetKey: `${row.code}-graded` });
                    }}
                    className={`rounded-md px-2 py-1 text-[11px] font-semibold ${
                      l.primary
                        ? "bg-primary text-primary-foreground hover:brightness-110"
                        : "border border-border/60 hover:border-primary/60"
                    }`}
                  >
                    {l.label}
                  </a>
                ))}
                {GRADING_SOURCES.slice(0, 1).map((s) => (
                  <a
                    key={s.id}
                    href={s.pop}
                    target="_blank"
                    rel="nofollow ugc noopener noreferrer"
                    title={s.note}
                    onClick={(e) => e.stopPropagation()}
                    className="rounded-md border border-border/60 px-2 py-1 text-[11px] font-semibold hover:border-primary/60"
                  >
                    PSA pop
                  </a>
                ))}
              </div>
              <p className="mt-2 text-[10px] leading-snug text-muted-foreground">
                Graded prices are tracked separately and never blended into the raw market value. eBay links are
                affiliate links; Pal Stars may earn a commission.
              </p>
            </div>,
            document.body,
          )
        : null}
    </span>
  );
}
